import { memo, useState } from 'react';

import { CheckIcon, ChevronDown, XCircle, XIcon } from 'lucide-react';
import { AnimatePresence, motion, type Transition } from 'motion/react';

import { Badge } from '@/shadcn/badge';
import { Button } from '@/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/shadcn/command';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Separator } from '@/shadcn/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import type { MultiSelectOption } from '@/components/forms/components/form/writer/widgets/multiSelectMenu/multiSelectMenu.writer';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';

const transition: Transition = {
  type: 'spring',
  duration: 0.3,
  bounce: 0.1
};

const DEBOUNCE_TIME = 2000;

type Props = WidgetEditorBaseProps<string[]> & {
  options: MultiSelectOption[] | string[] | undefined;
};

export const MultiselectEditor = memo(
  ({
    value,
    identifier,
    onChange,
    options: rawOptions = [],
    placeholder,
    header = '',
    disabled = false
  }: Props): JSX.Element => {
    // focus state
    const isInBatchEdit = !!header;

    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const onValueChange = (value: string[]) => {
      const handler = setTimeout(() => {
        onChange(value);
      }, DEBOUNCE_TIME);

      return () => {
        clearTimeout(handler);
      };
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter(value => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen(prev => !prev);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map(option => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    const options: MultiSelectOption[] = rawOptions.map(option => {
      if (typeof option === 'string') {
        return { label: option, value: option, iconName: undefined };
      }
      return option;
    });

    if (disabled) {
      return (
        <div
          className="scrollbar-thin size-full overflow-auto px-3 py-2 text-sm"
          data-cy={`multiselect-editor-${identifier}-disabled`}
        >
          {options
            .filter(option => value?.includes(option.value))
            .map(option => option.label)
            .join(', ')}
        </div>
      );
    }

    return (
      <Popover
        modal={false}
        onOpenChange={setIsPopoverOpen}
        open={isPopoverOpen}
      >
        <PopoverTrigger asChild>
          <div
            className={cn('flex flex-col gap-2', !isInBatchEdit && 'size-full')}
          >
            {isInBatchEdit && (
              <Label
                data-cy={`multiselect-editor-${identifier}-label`}
                htmlFor={identifier}
              >
                {header || '🚨 No provided header'}
              </Label>
            )}
            <Button
              aria-expanded={isPopoverOpen}
              aria-haspopup="listbox"
              aria-label={`Click to open the ${header ?? identifier} multi-select menu`}
              className={cn(
                'flex size-full items-center justify-between overflow-hidden rounded-md border border-input bg-transparent p-0 font-normal normal-case tracking-normal hover:bg-inherit active:scale-100 disabled:pointer-events-auto disabled:cursor-auto disabled:opacity-70 [&_svg]:pointer-events-auto',
                selectedValues.length === 0 && 'hover:bg-accent',
                isInBatchEdit ? 'h-9' : 'rounded-none border-0'
              )}
              data-cy={`multiselect-editor-${identifier}-button`}
              id={`multiselect-editor-${identifier}-button`}
              onClick={handleTogglePopover}
            >
              {selectedValues.length > 0 ? (
                <div className="flex h-full w-full flex-row items-center justify-between overflow-hidden">
                  <div className="scrollbar-thin relative flex h-full flex-row items-center gap-1.5 overflow-hidden overflow-x-auto px-1 text-sm">
                    <AnimatePresence mode="popLayout">
                      <TooltipProvider>
                        {selectedValues.map(value => {
                          const option = options.find(o => o.value === value);
                          const id = `${identifier}-${value}`;
                          return (
                            <motion.div
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{
                                opacity: 0,
                                scale: 0.5
                              }}
                              initial={{
                                opacity: 0,
                                scale: 0.8
                              }}
                              key={id}
                              layout
                              transition={transition}
                            >
                              <Badge
                                className="h-4.5 rounded-full bg-accent px-2 text-[10px] text-accent-foreground hover:shadow dark:hover:shadow-lg"
                                data-cy={`multiselect-editor-${identifier}-badge-${value}`}
                                variant="card"
                              >
                                {option?.iconName && (
                                  <div className="mr-1">
                                    <DynamicIcon
                                      icon={option.iconName}
                                      size={14}
                                    />
                                  </div>
                                )}
                                {option?.label}

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className="group ml-1 flex size-4 cursor-pointer items-center justify-center rounded-full"
                                      data-cy={`multiselect-editor-${identifier}-item-${value}-clear`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        toggleOption(value);
                                      }}
                                    >
                                      <XCircle className="h-3! text-muted-foreground transition-colors group-hover:text-foreground" />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Remove the{' '}
                                    <b className="font-semibold">
                                      {option?.label}
                                    </b>{' '}
                                    item
                                  </TooltipContent>
                                </Tooltip>
                              </Badge>
                            </motion.div>
                          );
                        })}
                      </TooltipProvider>
                    </AnimatePresence>
                  </div>

                  <div className="mr-2 flex h-full shrink-0 items-center justify-between gap-2">
                    <Separator
                      className="flex h-full min-h-6 px-0"
                      orientation="vertical"
                    />
                    <TooltipProvider>
                      {isPopoverOpen ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <XIcon
                              aria-label="Deselect all options"
                              className="h-3 cursor-pointer text-muted-foreground hover:text-destructive"
                              data-cy={`multiselect-editor-${identifier}-deselect-all`}
                              onClick={event => {
                                event.stopPropagation();
                                handleClear();
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>Deselect all</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ChevronDown className="h-3 origin-top cursor-pointer text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>Open options</TooltipContent>
                        </Tooltip>
                      )}
                    </TooltipProvider>
                  </div>
                </div>
              ) : (
                <div className="mr-2 ml-3 flex w-full items-center justify-between">
                  <span
                    className="text-muted-foreground text-sm"
                    data-cy={`multiselect-editor-${identifier}-placeholder`}
                  >
                    {placeholder || 'Select option(s)'}
                  </span>
                  <ChevronDown className="h-4 cursor-pointer text-muted-foreground" />
                </div>
              )}
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-0"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              data-cy={`multiselect-editor-${identifier}-input`}
              onKeyDown={handleInputKeyDown}
              placeholder="Search..."
            />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  className="cursor-pointer"
                  key="all"
                  onSelect={toggleAll}
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input',
                      selectedValues.length === rawOptions.length
                        ? 'bg-primary'
                        : 'opacity-50 [&_svg]:invisible'
                    )}
                    data-cy={`multiselect-editor-${identifier}-select-all`}
                  >
                    <CheckIcon className="h-4 w-4 text-white" />
                  </div>
                  <span>(Select All)</span>
                </CommandItem>
                {options.map(option => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      className="cursor-pointer"
                      data-cy={`multiselect-editor-${identifier}-item-${option.value}`}
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-input [&svg]:text-white',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className="h-4 w-4 text-white" />
                      </div>
                      {option.iconName && (
                        <div
                          className="mr-2 text-muted-foreground"
                          data-cy={`multiselect-editor-${identifier}-item-${option.value}-icon`}
                        >
                          <DynamicIcon icon={option.iconName} size={16} />
                        </div>
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        className="flex-1 cursor-pointer justify-center"
                        data-cy={`multiselect-editor-${identifier}-clear-btn`}
                        onSelect={handleClear}
                      >
                        Clear
                      </CommandItem>
                      <Separator
                        className="flex h-full min-h-6"
                        orientation="vertical"
                      />
                    </>
                  )}
                  <CommandItem
                    className="max-w-full flex-1 cursor-pointer justify-center"
                    data-cy={`multiselect-editor-${identifier}-close-btn`}
                    onSelect={() => setIsPopoverOpen(false)}
                  >
                    Close
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);
